import { Text } from "@clipboard-health/ui-react";
import { Stack } from "@mui/material";
import { addHours, addMinutes, formatDistanceToNow, isAfter } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { TripStatus } from "../../../../containers/facilityDashboard/ShiftCalendar/dayView/workerETA/types";
import { useIsSmallScreen } from "../../hooks/useIsSmallScreen";
import { useShiftCardContext } from "../useShiftCard";

export function ShiftBoardLiveTrackerCardHeader() {
  const {
    shift,
    workplace,
    isWorkerClockedIn,
    isShiftEnded,
    tripHasStarted,
    isTwoHoursBeforeShiftStart,
    distanceInMiles,
    chips,
  } = useShiftCardContext();
  const { trip, clockInOut, start } = shift;
  const isSmallScreen = useIsSmallScreen();

  const { status, eta, lastLocationReceivedByServerAt } = trip ?? {};

  const isShiftCancelled = chips?.some((chip) => chip.status === "Cancelled");
  const isWorkerAtHome = chips?.some((chip) => chip.status === "Worker at home");
  const isShiftCompleted = chips?.some((chip) => chip.status === "Shift completed");
  const isArrived = chips?.some((chip) => chip.status === "Arrived");

  if (isShiftCancelled) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
        Shift cancelled; live tracking has been turned off
      </Text>
    );
  }

  if (isArrived) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
        Worker has arrived! Live tracking has been turned off
      </Text>
    );
  }

  if (isWorkerAtHome) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
        Worker is still at home, live tracking will be shown as soon as worker moves
      </Text>
    );
  }

  if (isShiftEnded || isShiftCompleted) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
        Shift ended; live tracking has been turned off
      </Text>
    );
  }

  if (isWorkerClockedIn) {
    return (
      <Text variant="body2">
        Worker{" "}
        <span style={{ color: "green", fontWeight: "bold" }}>
          clocked in at {formatInTimeZone(clockInOut!.clockIn!, workplace.timeZone, "hh:mm a")}
        </span>
        ; live tracking has been turned off
      </Text>
    );
  }

  if (isTwoHoursBeforeShiftStart) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
        Live tracking will be available 2 hours before shift start, at{" "}
        <span style={{ color: "green", fontWeight: "bold" }}>
          {formatInTimeZone(addHours(start, -2), workplace.timeZone, "hh:mm a")}
        </span>
      </Text>
    );
  }

  if (status === TripStatus.TRIP_NO_INFORMATION) {
    return (
      <Stack direction="column" spacing={0.5} alignItems="end">
        <Text semibold variant="body2" color="error">
          We&apos;re having trouble connecting with this worker&apos;s device.
        </Text>
        <Text semibold variant="body2" color="secondary">
          Last retried 1 minute ago
        </Text>
      </Stack>
    );
  }

  return (
    <Stack alignItems={isSmallScreen ? "center" : "end"} paddingTop={isSmallScreen ? 2 : 0}>
      <Stack direction="row" gap={2}>
        <Text variant="body2">
          Distance: <span style={{ color: "black" }}>{Math.round(distanceInMiles ?? 0)} mi</span>
        </Text>
        <Text variant="body2">
          ETA: <span style={{ color: "black" }}>{Math.round(eta?.durationInMin ?? 0)} mins</span>
        </Text>
      </Stack>
      <Text variant="body2">
        Last Updated:{" "}
        {lastLocationReceivedByServerAt
          ? `${formatDistanceToNow(lastLocationReceivedByServerAt)} ago`
          : "N/A"}
        {lastLocationReceivedByServerAt &&
          tripHasStarted &&
          isAfter(addMinutes(new Date(), -5), lastLocationReceivedByServerAt) && (
            <>
              .{" "}
              <span style={{ color: "red", fontStyle: "italic" }}>
                We seem to have lost connection.
              </span>
            </>
          )}
      </Text>
    </Stack>
  );
}
